import type { ActorIdentity } from "@/components/app/entities/actor-hover-view";
import type {
  CurrentMlxUser,
  WorkspaceFeedItem,
  WorkspaceOrganizationMember,
  WorkspaceSession,
} from "@/lib/mlx-workspace-client";

export type ActorDisplay = {
  name: string;
  initials: string;
  imageUrl: string | null;
  icon?: "twin" | "key";
  /** Who is behind the name, for their card; a connector has none. */
  identity?: ActorIdentity;
};
export type FeedMemberDisplay = Pick<
  WorkspaceOrganizationMember,
  "name" | "email" | "imageUrl"
> & Partial<Pick<WorkspaceOrganizationMember, "userId">>;

export function resolveFeedActor(
  actor: WorkspaceFeedItem["actor"],
  membersById: Map<string, FeedMemberDisplay>,
  selfIds: Set<string>,
  currentUser: CurrentMlxUser | null,
): ActorDisplay {
  const ids = [actor.subjectId, actor.id].filter((id): id is string =>
    Boolean(id?.trim()),
  );
  const member = ids
    .map((id) => membersById.get(id) ?? membersById.get(id.toLocaleLowerCase()))
    .find(Boolean);
  if (
    ids.some(
      (id) =>
        selfIds.has(id) ||
        id.toLocaleLowerCase() === currentUser?.email?.toLocaleLowerCase(),
    ) ||
    // An alias the caller's own ids miss still names them when the directory
    // entry it finds is theirs, so one list never says both "You" and their name.
    (member !== undefined && memberIsCurrentUser(member, currentUser))
  ) {
    return currentUserFeedActor(currentUser, member, ids[0]);
  }
  const name = member?.name?.trim() || member?.email?.trim();
  if (name)
    return {
      name,
      initials: name
        .split(/[\s@._-]+/u)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join(""),
      imageUrl: member?.imageUrl ?? null,
      identity: { kind: "person", userId: member?.userId ?? ids[0] ?? null, name, imageUrl: member?.imageUrl ?? null },
    };
  if (actor.type === "agent")
    return { name: "Agent", initials: "AI", imageUrl: null, identity: { kind: "twin" } };
  if (actor.type === "service" && actor.apiKeyLabel?.trim())
    return {
      name: `${actor.apiKeyLabel.trim()} (API key)`,
      initials: "API",
      imageUrl: null,
      icon: "key",
      identity: { kind: "apiKey", label: actor.apiKeyLabel.trim() },
    };
  if (actor.type === "service")
    return { name: "Connector", initials: "⇄", imageUrl: null };
  if (actor.type === "user")
    // Nobody the directory knows: no card, rather than a card about no one.
    return { name: "Unknown person", initials: "?", imageUrl: null };
  return { name: "Twin", initials: "T", imageUrl: null, icon: "twin", identity: { kind: "twin" } };
}

/** The reader, as a row names them: "You", with their picture. */
export function currentUserFeedActor(
  currentUser: CurrentMlxUser | null,
  member?: FeedMemberDisplay,
  fallbackId?: string,
): ActorDisplay {
  const imageUrl = member?.imageUrl ?? currentUser?.imageUrl ?? null;
  return {
    name: "You",
    initials: "You",
    imageUrl,
    identity: { kind: "person", userId: member?.userId ?? currentUser?.id ?? fallbackId ?? null, name: currentUser?.name?.trim() || "You", imageUrl },
  };
}

export const TWIN_FEED_ACTOR: ActorDisplay = {
  name: "Twin",
  initials: "T",
  imageUrl: null,
  icon: "twin",
  identity: { kind: "twin" },
};

/**
 * Work a Twin run did for a person reads as Twin's, for them: Twin's mark, and
 * "Twin, for you, updated …". A row the person did themselves keeps their name.
 */
export function twinWorkFor(
  person: ActorDisplay,
): { actor: ActorDisplay; onBehalfOf: string } | null {
  if (person.identity?.kind !== "person") return null;
  return {
    actor: TWIN_FEED_ACTOR,
    onBehalfOf: person.name === "You" ? "you" : person.name,
  };
}

function memberIsCurrentUser(
  member: FeedMemberDisplay,
  currentUser: CurrentMlxUser | null,
): boolean {
  if (!currentUser) return false;
  if (member.userId && member.userId === currentUser.id) return true;
  const email = currentUser.email?.trim().toLocaleLowerCase();
  return Boolean(email && member.email?.trim().toLocaleLowerCase() === email);
}

export function sessionFeedActor(
  session: WorkspaceSession,
  currentUser: CurrentMlxUser | null,
  members: Map<string, FeedMemberDisplay>,
): ActorDisplay {
  const ids = [
    session.pluginSubjectId,
    session.verifiedSubjectId,
    session.userId,
  ].filter((id): id is string => Boolean(id));
  const selfIds = new Set(currentUser ? [currentUser.id] : []);
  const identity =
    ids.find(
      (id) =>
        selfIds.has(id) ||
        members.has(id) ||
        members.has(id.toLocaleLowerCase()),
    ) ?? ids[0];
  if (!identity) return { name: "Twin", initials: "T", imageUrl: null };
  return resolveFeedActor(
    {
      id: identity,
      subjectId: null,
      type: "user",
      clientApp: null,
      interfaceType: null,
      executedByTwin: false,
    },
    members,
    selfIds,
    currentUser,
  );
}
